import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Button } from "@wac-ui-dashboard/wac_component_library";
import { getFormatedBusinessDate } from "../../../../utils/Functions/table";
import { updateConfig } from "../../../../store/slices/User/UserDetails/userSubscriptionSlice";
import { useGetCustomerSubscriptionListDataQuery } from "../../../../store/queries/Subscription";

/**
 * Columns the whole Customer Subscriptions screen needs but this tab does not: the customer
 * is the page you are already on. Hidden rather than dropped, so the saved preferences stay
 * one shared set for the module instead of two that can disagree.
 */
const REDUNDANT_ON_PROFILE = ["customer_name", "customer_mobile"];

const useSubscriptions = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];
  const dispatch = useDispatch();
  const { userID } = useParams();
  const activeProfile = userID ?? sessionStorage.getItem("active");
  const subscriptionState = useSelector((state) => state.userSubscription);

  const {
    data: subscriptionList = {},
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetCustomerSubscriptionListDataQuery(
    {
      user_id: activeProfile,
      page_size: subscriptionState?.currentPageSize,
      page: subscriptionState?.currentPage,
      sort: subscriptionState?.sortBy,
      sort_order: subscriptionState?.sortOrder,
      search: subscriptionState?.search,
    },
    { skip: !activeProfile }
  );

  /**
   * This tab opens with a profile, but the endpoint behind it is gated by
   * manage_customer_subscriptions - a different permission from the one that got the admin
   * here. A refusal has to say so: rendering the empty state would tell them this customer
   * has never bought a plan, which is a different and possibly untrue statement.
   */
  const isForbidden = error?.status === 403 || error?.status === 401;

  const fields = useMemo(() => {
    const all = subscriptionList?.fields ?? {};

    return Object.keys(all).reduce((acc, key) => {
      acc[key] = REDUNDANT_ON_PROFILE.includes(key)
        ? { ...all[key], hidden: true }
        : all[key];
      return acc;
    }, {});
  }, [subscriptionList?.fields]);

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };

  const handleClearClick = () => {
    dispatch(
      updateConfig((state) => {
        state.search = "";
        state.currentPage = 1;
      })
    );
  };

  const handleSort = (label) => {
    if (subscriptionState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder =
            subscriptionState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };

  const openAction = (modal, subscription) => {
    dispatch(
      updateConfig((state) => {
        state[modal] = true;
        state.selectedId = subscription?.subscription_id ?? "";
        state.selectedSubscription = subscription ?? {};
      })
    );
  };

  const closeAction = (modal) => {
    dispatch(
      updateConfig((state) => {
        state[modal] = false;
      })
    );
  };

  /**
   * The same three actions as the Customer Subscriptions screen. This tab was read-only on
   * the grounds that anyone who can open a profile can reach it - but the listing behind it
   * is the same manage_customer_subscriptions endpoint, so an admin without that permission
   * never gets rows here to act on, they get the refusal above. Support answering the phone
   * arrives at the customer, not at a list of every subscription sold.
   */
  const getRow = (field, data) => {
    // Same columns as the Customer Subscriptions screen - it is the same module - so the
    // dates are read the same way here.
    const dateCell = (field, data) => (
      <p className="pro-mb-0">{getFormatedBusinessDate(data?.[field])}</p>
    );

    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      expires_at: dateCell,
      purchased_at: dateCell,
      activated_at: dateCell,
      created_at: dateCell,
      remaining_items: (field, data) => (
        <p className="pro-mb-0">
          {data?.remaining_items} of {data?.total_items}
        </p>
      ),
      action: (field, data) => (
        <div className="pro-d-flex pro-gap-2 no-wrap">
          <Button
            className={"pro-btn-link sm"}
            onClick={() => openAction("showDetailsModal", data)}
          >
            View
          </Button>
          {/*
            Cancelling and crediting are only offered where they mean something - a
            cancelled subscription has already had its balance written off and its refund
            settled, and reopening that conversation from a button would only confuse it.
          */}
          {data?.status_label !== "Cancelled" && (
            <>
              <Button
                className={"pro-btn-link sm"}
                onClick={() => openAction("showCreditModal", data)}
              >
                Credit
              </Button>
              <Button
                className={"pro-btn-link sm"}
                onClick={() => openAction("showCancelModal", data)}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      ),
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  return {
    isLoading,
    isFetching,
    isForbidden,
    hasError: Boolean(error),
    fields,
    subscriptionList,
    subscriptionState,
    paginationOptions,
    handlePagination,
    handleClearClick,
    handlePageSize,
    handleSearch,
    closeAction,
    handleSort,
    refetch,
    getRow,
  };
};

export default useSubscriptions;
